import { useDispatch, useSelector } from "react-redux";
import LocalVideoPreview from "../LocalVideoPreview";
import {
  autoFormatText,
  correctImageOrientation,
  rotateImageFile90,
  todayFullStringDate,
  resolveUserAvatarSrc,
} from "../../services/functions";
import { toErrorText } from "../../services/errorText";
import { useRef, useEffect, useState } from "react";
import {
  IonActionSheet,
  IonAvatar,
  IonButton,
  IonFooter,
  IonIcon,
  IonItem,
  IonTextarea,
  IonToast,
  useIonLoading,
  useIonToast,
} from "@ionic/react";
import {
  image,
  sendSharp,
  cameraOutline,
  imagesOutline,
  videocamOutline,
  arrowUndoOutline,
  arrowRedoOutline,
} from "ionicons/icons";
import { useMediaPicker } from "../../hooks/useMediaPicker";
import { ImageLoader } from "../ImageLoader";
import {
  commentPost,
  getPost,
  headersDefault,
  replyComment,
  baseUrl,
} from "../../redux/actions";
import config from "../../settings/config";
import "./InsertComment.css";
import { useHistory } from "react-router";
import { useAppPermission } from "../../hooks/useAppPermission";
import PermissionPopupModal from "../modals/PermissionPopupModal/PermissionPopupModal";
import { PERMISSION_ACTIONS } from "../../constants/permissionActions";
import { MAX_COMMENT_LENGTH } from "../../constants/commentLimits";
import { getAuthToken } from "../../services/authStorage";
import { useDraft } from "../../hooks/useDraft";
import { draftKey } from "../../utils/drafts";

import {
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  maxBytesForFile,
  maxMbLabel,
} from "../../constants/mediaLimits";
let headers = {};

export const InsertComment = ({
  element_id,
  element_type,
  replyToName, // when replying to a reply: name shown as "In risposta a …"
  contentRef,
  setComments,
  onReplyAdded,
  variant, // "sheet" → modern avatar + pill composer
  // Told whenever a photo or clip is attached or removed. The comments sheet
  // sizes itself from the comment count alone, so a short sheet had no room for
  // the preview and pushed the send button off the bottom of the screen —
  // the attachment was chosen and could then never be sent.
  onAttachmentChange,
}) => {
  const fullUser = useSelector((state) => state.user.user);
  const mySubscriber = useSelector((state) => state.user?.master_club_subscriber);
  const user = {
    name: fullUser.name,
    surname: fullUser.surname,
    avatar: fullUser.avatar,
    id: fullUser.id,
    fullname: fullUser.fullname ?? "",
  };

  // The create/reply API responses don't always embed the author relation.
  // Without it the Comment/PostFooter renderers crash (they read
  // created_by.id / .subscriber_full_name). Backfill it from the current user
  // so the optimistic row renders correctly until the next full load.
  const enrichComment = (c) => {
    if (!c) return c;
    if (c.created_by && c.created_by.id) return c;
    const fullname =
      fullUser.fullname ||
      [fullUser.name, fullUser.surname].filter(Boolean).join(" ").trim();
    return {
      ...c,
      created_by_user_id: c.created_by_user_id ?? fullUser.id,
      created_by: {
        id: fullUser.id,
        subscriber_full_name: fullname,
        avatar: fullUser.avatar,
        master_club_subscriber: mySubscriber,
        ...(c.created_by || {}),
      },
    };
  };

  const {
    checkPermission,
    isModalOpen,
    setIsModalOpen,
    popupText,
    isBlocked: isCommentBlocked,
    isLoaded: isCommentPermissionLoaded,
  } = useAppPermission(PERMISSION_ACTIONS.FEED_COMMUNITY_COMMENT);

  const isCommentInputDisabled =
    isCommentPermissionLoaded && isCommentBlocked;

  const inputValue = useRef(null);
  const dispatch = useDispatch();
  const { telephone } = useSelector((state) => state.user.user);
  const [ionToast] = useIonToast();
  const history = useHistory();

  // ── @mention autocomplete ──────────────────────────────────────────────────
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const mentionRangeRef = useRef(null); // { start, end } of the active @partial
  const mentionDebounceRef = useRef(null);
  const mentionsRef = useRef([]); // inserted mentions: [{ id, nickname }]
  const nativeInputRef = useRef(null); // cached native <textarea> for sync caret reads

  // The unsent comment, one per post and one per thread being replied to. The
  // sheet unmounts this composer on every close, reply and "Annulla", which
  // used to throw the text away.
  const commentDraft = useDraft(
    draftKey("comment", fullUser?.id, element_type, element_id),
  );
  const saveDraft = () =>
    commentDraft.save({
      text: inputValue.current?.value ?? "",
      mentions: mentionsRef.current,
    });

  useEffect(() => {
    const el = inputValue.current;
    const saved = commentDraft.load();
    if (!el || !saved || el.value) return;
    el.value = saved.text;
    mentionsRef.current = saved.mentions;
  }, [commentDraft]);

  const closeMentions = () => {
    setShowMentions(false);
    setMentionSuggestions([]);
    mentionRangeRef.current = null;
  };

  const fetchMentions = (query) => {
    if (mentionDebounceRef.current) clearTimeout(mentionDebounceRef.current);
    mentionDebounceRef.current = setTimeout(() => {
      fetch(
        `${baseUrl}/api/master-club-subscriber/mention-search?q=${encodeURIComponent(
          query,
        )}&limit=8`,
        { headers: headersDefault() },
      )
        .then((r) => (r.ok ? r.json() : { data: [] }))
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          setMentionSuggestions(list);
          setShowMentions(list.length > 0);
        })
        .catch(() => closeMentions());
    }, 200);
  };

  /**
   * Keep the line being typed inside the box.
   *
   * Once the composer reaches its max-height the native textarea scrolls
   * internally, but WebKit leaves `scrollTop` at 0 after Ionic's autoGrow has
   * capped the height, so on iOS everything past the sixth line was typed
   * blind: measured on an iPhone 17 Pro simulator, `scrollHeight 211 /
   * clientHeight 183 / scrollTop 0` — the caret's line sat below the visible
   * area, behind the keyboard (client, 23/09/2026).
   *
   * Only follows a caret that is at the very end, which is what typing does;
   * editing in the middle keeps whatever the browser already shows.
   */
  const keepCaretInView = (native, caret) => {
    if (!native) return;

    requestAnimationFrame(() => {
      const value = native.value || "";
      // Nothing typed yet: scrolling an empty box only pushes the placeholder
      // out of sight, which on a narrow screen (where it wraps) reads as a
      // truncated "Scrivi un…".
      if (!value) return;
      if (caret < value.length) return;
      if (native.scrollHeight <= native.clientHeight) return;

      native.scrollTop = native.scrollHeight - native.clientHeight;
    });
  };

  // Detect a trailing "@partial" before the caret and drive the suggestions.
  const handleCommentInput = async () => {
    const el = inputValue.current;
    if (!el) return;
    saveDraft();
    const value = el.value || "";
    let caret = value.length;
    try {
      const native = await el.getInputElement?.();
      if (native) {
        nativeInputRef.current = native;
        if (typeof native.selectionStart === "number") {
          caret = native.selectionStart;
        }
      }
    } catch (e) {
      // fall back to end-of-value caret
    }

    keepCaretInView(nativeInputRef.current, caret);

    const before = value.slice(0, caret);
    // Nicknames are free-form (min:4/max:32, no char restriction) so match any
    // run of non-whitespace, non-@ chars after the "@" (covers $, ., symbols…).
    const match = before.match(/(^|\s)@([^\s@]{1,50})$/u);
    if (match) {
      const partial = match[2];
      mentionRangeRef.current = { start: caret - partial.length - 1, end: caret };
      fetchMentions(partial);
    } else {
      closeMentions();
    }
  };

  const insertMention = async (mentionUser) => {
    const el = inputValue.current;
    const range = mentionRangeRef.current;
    // Prefer the @nickname handle; fall back to the full name for users who
    // haven't set a nickname (they're still returned by the premium mention
    // search, so selecting them must work — record the id and insert a token
    // that round-trips: payload keeps mentions whose `@handle` is in the text,
    // and renderTextWithMentions matches on the same handle to colour it).
    const handle =
      mentionUser?.nickname?.trim() ||
      [mentionUser?.name, mentionUser?.surname].filter(Boolean).join(" ").trim();
    if (!el || !range || !handle) return closeMentions();
    const value = el.value || "";
    const inserted = `@${handle} `;
    el.value = value.slice(0, range.start) + inserted + value.slice(range.end);
    // Track the mention so we can send its user id with the comment (deduped).
    if (
      mentionUser?.id != null &&
      !mentionsRef.current.some((m) => m.id === mentionUser.id)
    ) {
      mentionsRef.current.push({ id: mentionUser.id, nickname: handle });
    }
    closeMentions();
    saveDraft();
    const newCaret = range.start + inserted.length;
    try {
      const native = await el.getInputElement?.();
      if (native) {
        native.focus();
        native.setSelectionRange(newCaret, newCaret);
      }
    } catch (e) {
      // best-effort caret restore
    }
  };

  // Mentions behave as atomic tokens: pressing Backspace/Delete anywhere inside
  // (or right after) an @nickname removes the WHOLE "@nickname " token and
  // untracks it, instead of nibbling one character. Runs synchronously off the
  // cached native element so preventDefault takes effect.
  const handleCommentKeyDown = (e) => {
    if (e.key !== "Backspace" && e.key !== "Delete") return;
    if (!mentionsRef.current.length) return;
    const el = inputValue.current;
    const native = nativeInputRef.current;
    if (!el || !native) return;
    if (native.selectionStart !== native.selectionEnd) return; // only collapsed caret
    const value = el.value || "";
    const caret = native.selectionStart;
    if (caret == null) return;

    for (const m of mentionsRef.current) {
      const token = `@${m.nickname}`;
      let idx = value.indexOf(token);
      while (idx !== -1) {
        const start = idx;
        const end = idx + token.length;
        const inRange =
          e.key === "Backspace"
            ? caret > start && caret <= end
            : caret >= start && caret < end;
        if (inRange) {
          e.preventDefault();
          let removeEnd = end;
          if (value[removeEnd] === " ") removeEnd += 1; // eat one trailing space
          el.value = value.slice(0, start) + value.slice(removeEnd);
          mentionsRef.current = mentionsRef.current.filter((x) => x.id !== m.id);
          saveDraft();
          try {
            native.focus();
            native.setSelectionRange(start, start);
          } catch (err) {
            // best-effort caret restore
          }
          closeMentions();
          return;
        }
        idx = value.indexOf(token, idx + 1);
      }
    }
  };

  const mentionsDropdown = showMentions ? (
    <div className="mention-suggestions">
      {mentionSuggestions.map((u) => (
        <button
          type="button"
          key={u.id ?? u.nickname}
          className="mention-suggestion"
          // onMouseDown (not onClick) so the textarea doesn't blur/close first
          onMouseDown={(e) => {
            e.preventDefault();
            insertMention(u);
          }}
        >
          <IonAvatar className="mention-suggestion-avatar">
            <ImageLoader imageType="user" image={u.avatar} fallbackName={u} />
          </IonAvatar>
          <span className="mention-suggestion-text">
            {u.nickname ? (
              <span className="mention-suggestion-nick">@{u.nickname}</span>
            ) : null}
            {(u.name || u.surname) && (
              <span className="mention-suggestion-name">
                {[u.name, u.surname].filter(Boolean).join(" ")}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  ) : null;

  // After publishing, take the user to the new comment/reply. The comment only
  // appears after the state update re-renders, so poll for the element (a fixed
  // timeout raced the render and often missed it) before scrolling.
  const scrollToComment = (id, attempt = 0) => {
    const el = document.getElementById(`comment-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      return;
    }
    if (attempt < 20) {
      requestAnimationFrame(() => scrollToComment(id, attempt + 1));
    }
  };

  const handleClickSendCommentButton = () => {
    const { allowed } = checkPermission();
    if (!allowed) return;

    // Same light auto-formatting as posts: capitalize the first letter and the
    // start of each new sentence (after . ! ?) and ensure a space after
    // punctuation. Applied at submit so it never fights the caret while typing.
    const content = autoFormatText(
      inputValue.current.value.trim().slice(0, MAX_COMMENT_LENGTH),
    );
    // An attachment alone is enough to send — a clip [24] counts just like an image.
    const hasImage =
      !!correctedFileRef.current ||
      !!videoFileRef.current ||
      document.getElementById("comment-upload-image").files[0] !== undefined;
    if (content === "" && !hasImage) return;
    uploadImage()
      .then((uploaded) => {
        // The upload returns one filename; which field it lands in depends on
        // what was staged — a comment carries a clip [24] OR an image.
        const isVideo = !!videoFileRef.current;
        const values = {
          content,
          element_id,
          user,
          image: isVideo ? null : uploaded,
          video: isVideo ? uploaded : null,
          created_at: todayFullStringDate(),
          // Only keep mentions whose @nickname token is still present in the
          // final text — if the user edited/removed it, the mention is dropped.
          mentions: mentionsRef.current.filter((m) =>
            content.includes(`@${m.nickname}`),
          ),
          // Reply-to-reply: persist who this reply targets ("In risposta a …").
          reply_to_name: element_type === "comment" ? replyToName || null : null,
        };

        // A rejected thunk RESOLVES with a rejected action rather than
        // throwing, so the .catch below never sees it: a refused comment used
        // to vanish silently, input cleared, with nothing said. Anything the
        // server refuses — the Community block above all, whose wording it
        // supplies itself — is surfaced here instead.
        // What was sent, so a refusal can hand it back: the box is emptied
        // below, before the server has answered.
        const sent = {
          text: inputValue.current?.value ?? "",
          mentions: [...mentionsRef.current],
        };
        const showIfRefused = (action) => {
          if (action?.meta?.requestStatus !== "rejected") return false;
          setToastIsOpen(toErrorText(action?.error));
          commentDraft.save(sent);
          if (inputValue.current && !inputValue.current.value) {
            inputValue.current.value = sent.text;
            mentionsRef.current = sent.mentions;
          }
          return true;
        };

        if (element_type === "comment") {
          // Handle replies
          dispatch(replyComment(values)).then((action) => {
            if (showIfRefused(action)) return;
            const newReply = enrichComment(action?.payload?.comment);
            // Sheet drill-in: inject the reply in place (CommentPage can't).
            if (newReply && onReplyAdded) onReplyAdded(newReply);
            if (newReply) scrollToComment(newReply.id);
          });
        } else {
          // Handle post comments
          dispatch(commentPost(values)).then((action) => {
            if (showIfRefused(action)) return;
            const newComment = enrichComment(action?.payload?.comment);
            if (newComment && setComments) {
              setComments((prevComments) => [newComment, ...prevComments]);
              scrollToComment(newComment.id);
            }
          });
        }

        inputValue.current.value = "";
        mentionsRef.current = [];
        deleteLoadedImage();
        commentDraft.clear();
      })
      .catch((e) => {
        // Was setToastIsOpen(e) — passing the Error object straight to IonToast
        // rendered as "undefined". Always hand it a real string.
        setToastIsOpen(toErrorText(e));
      });
  };

  const handleKeyUp = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleClickSendCommentButton();
      return;
    }
    if (e.ctrlKey && e.key === "Enter") {
      handleClickSendCommentButton();
    }
  };


  const [present, dismiss] = useIonLoading();

  const defaultToast = {
    message: "Ops! C'è stato un errore. Riprova più tardi.",
    open: false,
  };

  const [toast, setToast] = useState(defaultToast);

  const setToastIsOpen = (message = defaultToast.message) => {
    // IonToast needs a plain string — an Error/boolean/object passed here is
    // what surfaced as "undefined" in the UI. Coerce defensively so no future
    // caller can reintroduce that.
    const text =
      typeof message === "string" && message.trim()
        ? message
        : toErrorText(message, defaultToast.message);
    setToast({
      message: text,
      open: true,
    });
  };

  useEffect(() => {
    headers = headersDefault();
  }, []);

  const uploadImage = () => {
    return new Promise((resolve, reject) => {
      // Uploads whichever attachment is staged — a clip [24] or an image. The
      // endpoint is the same (it's disk-driven), only the file differs.
      const isVideo = !!videoFileRef.current;
      const image =
        videoFileRef.current ||
        correctedFileRef.current ||
        document.getElementById("comment-upload-image").files[0];
      if ((!loadedImage && !loadedVideo) || !image) {
        resolve(null);
        return;
      }

      if (image.size > maxBytesForFile(image)) {
        // NOTE: must return — without it the oversized file was still uploaded
        // after rejecting, so the comment ended up in a broken half-sent state.
        reject(
          new Error(
            `${isVideo ? "Video" : "Immagine"} troppo grande. Puoi caricare fino a ${maxMbLabel(maxBytesForFile(image))}`,
          ),
        );
        return;
      }

      present({
        message: isVideo ? "Caricando video..." : "Caricando immagine...",
      });

      const body = new FormData();

      body.append("master-club-comment", image);

      fetch(
        `${
          config.env[config.env.mode].url
        }/api/file-upload/master-club-comment`,
        {
          body,
          method: "POST",
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      )
        .then((response) => {
          if (!response.ok) throw new Error("fetch error");
          return response.json();
        })
        .then((data) => {
          resolve(data.filename);
          dismiss();
        })
        .catch((e) => {
          // Was setToastIsOpen(true) — a boolean is not a message, so the toast
          // rendered empty/"undefined" instead of telling the user what failed.
          setToastIsOpen(toErrorText(e, "Impossibile caricare l'immagine."));
          dismiss();
          reject(e); // Reject with the error
        });
    });
  };

  const correctedFileRef = useRef(null); // upright JPEG to upload (EXIF baked in)
  const [loadedImage, setLoadedImage] = useState(null);
  // Comment video attachment [24]. Kept separate from the image so a comment
  // carries one OR the other, never both.
  const videoFileRef = useRef(null);
  const [loadedVideo, setLoadedVideo] = useState(null);

  // Comment clips are stored and played directly (no Vimeo transcoding), so we
  // cap the length — a long clip means a huge file and slow playback.
  const MAX_VIDEO_SECONDS = 60;

  const readVideoDuration = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const probe = document.createElement("video");
      probe.preload = "metadata";
      probe.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(probe.duration);
      };
      probe.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("unreadable video"));
      };
      probe.src = url;
    });

  const loadPickedVideo = async (file) => {
    if (!file) return;
    if (file.size > MAX_VIDEO_BYTES) {
      deleteLoadedImage();
      setToastIsOpen(
        `Video troppo grande. Puoi caricare fino a ${maxMbLabel(MAX_VIDEO_BYTES)}`,
      );
      return;
    }
    let duration = 0;
    try {
      duration = await readVideoDuration(file);
    } catch (e) {
      deleteLoadedImage();
      setToastIsOpen("Formato video non supportato.");
      return;
    }
    if (duration > MAX_VIDEO_SECONDS) {
      deleteLoadedImage();
      setToastIsOpen(
        `Video troppo lungo. Puoi caricare fino a ${MAX_VIDEO_SECONDS} secondi.`,
      );
      return;
    }
    // Swap out any image — one attachment per comment.
    correctedFileRef.current = null;
    setLoadedImage(null);
    videoFileRef.current = file;
    setLoadedVideo(URL.createObjectURL(file));
  };

  // Shared by the gallery <input> and (on native) the camera, so every path
  // uploads a physically-upright JPEG instead of the raw (often rotated) file.
  const loadPickedImage = async (file) => {
    if (!file) return;
    // The same input accepts clips now — route videos to their own handler.
    if ((file.type || "").startsWith("video/")) return loadPickedVideo(file);
    if (file.size > MAX_IMAGE_BYTES) {
      deleteLoadedImage();
      setToastIsOpen(
        `Immagine troppo grande. Puoi caricare fino a ${maxMbLabel(MAX_IMAGE_BYTES)}`,
      );
      return;
    }
    const corrected = await correctImageOrientation(file);
    videoFileRef.current = null;
    setLoadedVideo(null);
    correctedFileRef.current = corrected;
    setLoadedImage(URL.createObjectURL(corrected));
  };

  const selectedImage = (e) => loadPickedImage(e.target.files[0]);

  // Manually rotate the picked comment image 90° (same as the post composer) so
  // the user can fix an orientation EXIF didn't catch before sending. Re-encodes
  // the File and swaps the preview URL.
  const rotateCommentImage = async (direction) => {
    const current = correctedFileRef.current;
    if (!current) return;
    try {
      const rotated = await rotateImageFile90(current, direction);
      correctedFileRef.current = rotated;
      setLoadedImage((prev) => {
        if (prev) {
          try {
            URL.revokeObjectURL(prev);
          } catch {}
        }
        return URL.createObjectURL(rotated);
      });
    } catch {
      // keep the current image on failure
    }
  };

  // Rotate controls overlaid on the image preview. stopPropagation keeps a tap
  // on the arrows from triggering the preview's "Elimina" delete handler.
  const imageRotateBar = (
    <div
      className="comment-image-rotatebar"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label="Ruota a sinistra"
        onClick={() => rotateCommentImage("left")}
      >
        <IonIcon icon={arrowUndoOutline} />
      </button>
      <button
        type="button"
        aria-label="Ruota a destra"
        onClick={() => rotateCommentImage("right")}
      >
        <IonIcon icon={arrowRedoOutline} />
      </button>
    </div>
  );

  const [showPhotoSheet, setShowPhotoSheet] = useState(false);

  // Native pickers live in one shared hook (see hooks/useMediaPicker); web keeps
  // the file <input>. loadPickedImage already routes a clip to loadPickedVideo
  // by MIME type, so photos and videos can share the one entry point.
  const picker = useMediaPicker({
    onPicked: loadPickedImage,
    filename: "comment.jpg",
    videoBaseName: "comment",
  });

  const openPhotoPicker = () => {
    if (picker.isNative) {
      setShowPhotoSheet(true);
    } else {
      document.getElementById("comment-upload-image").click();
    }
  };

  const photoActionSheet = (
    <IonActionSheet
      isOpen={showPhotoSheet}
      onDidDismiss={() => setShowPhotoSheet(false)}
      header="Aggiungi una foto"
      buttons={[
        {
          text: "Fotocamera",
          icon: cameraOutline,
          handler: () => { picker.pickFromCamera(); },
        },
        {
          text: "Galleria",
          icon: imagesOutline,
          handler: () => { picker.pickFromGallery(); },
        },
        {
          // Picks the clip natively now. chooseFromGallery takes a
          // MediaTypeSelection, so this no longer has to detour through the file
          // input the way it did when the plugin returned stills only. The
          // comment input accepts video/quicktime, which is what iOS hands back.
          text: "Video",
          icon: videocamOutline,
          handler: () => { picker.pickVideoFromGallery(); },
        },
        { text: "Annulla", role: "cancel" },
      ]}
    />
  );

  const hasAttachment = !!loadedImage || !!loadedVideo;

  useEffect(() => {
    if (typeof onAttachmentChange === "function") {
      onAttachmentChange(hasAttachment);
    }
  }, [hasAttachment, onAttachmentChange]);

  const deleteLoadedImage = () => {
    correctedFileRef.current = null;
    setLoadedImage(null);
    // Clears the video attachment too — one shared "remove attachment" action.
    videoFileRef.current = null;
    setLoadedVideo(null);
    const el = document.getElementById("comment-upload-image");
    if (el) el.value = "";
  };

  // Modern Facebook-style composer used inside the comments bottom sheet:
  // current-user avatar + a rounded pill (photo icon inside) + send button.
  // Same refs/handlers/ids as the default composer, so all behaviour is shared.
  if (variant === "sheet") {
    const myAvatar = resolveUserAvatarSrc(fullUser);
    return (
      <>
        {loadedImage && (
          <div className="comment-image-preview sheet-composer-preview">
            <img src={loadedImage} alt="" />
            {imageRotateBar}
            <span onClick={deleteLoadedImage}>Elimina</span>
          </div>
        )}
        {loadedVideo && (
          <div className="comment-image-preview sheet-composer-preview">
            <LocalVideoPreview src={loadedVideo} />
            <span onClick={deleteLoadedImage}>Elimina</span>
          </div>
        )}
        <div className="sheet-composer">
          <IonAvatar className="sheet-composer-avatar">
            <ImageLoader imageType="user" image={myAvatar} fallbackName={user} />
          </IonAvatar>
          <div className="sheet-composer-pill insert-comment-mention-wrap">
            {mentionsDropdown}
            <IonTextarea
              ref={inputValue}
              className="comment-input sheet-composer-input"
              placeholder="Scrivi un commento…"
              autoGrow={true}
              rows={1}
              maxlength={MAX_COMMENT_LENGTH}
              spellcheck={true}
              autocapitalize="sentences"
              autocorrect="on"
              disabled={isCommentInputDisabled}
              onKeyUp={handleKeyUp}
              onKeyDown={handleCommentKeyDown}
              onIonInput={handleCommentInput}
              onIonFocus={async () => {
                try {
                  nativeInputRef.current =
                    await inputValue.current?.getInputElement?.();
                  // A restored draft opens with the caret at its end; show that
                  // end rather than the first lines.
                  keepCaretInView(
                    nativeInputRef.current,
                    (nativeInputRef.current?.value || "").length,
                  );
                } catch (e) {
                  // best-effort
                }
              }}
            ></IonTextarea>
            <input
              id="comment-upload-image"
              onChange={selectedImage}
              className="d-false"
              accept="image/png,image/jpeg,video/mp4,video/webm,video/quicktime"
              type="file"
            />
            <button
              type="button"
              className="sheet-composer-photo"
              aria-label="Aggiungi foto"
              disabled={isCommentInputDisabled}
              onClick={openPhotoPicker}
            >
              <IonIcon icon={image} />
            </button>
          </div>
          <button
            type="button"
            className="sheet-composer-send"
            aria-label="Invia"
            disabled={isCommentInputDisabled}
            onClick={handleClickSendCommentButton}
          >
            <IonIcon icon={sendSharp} />
          </button>
        </div>
        <IonToast isOpen={toast.open} message={toast.message} duration={3000} />
        {photoActionSheet}
        <PermissionPopupModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          message={popupText}
        />
      </>
    );
  }

  return (
    <>
      <IonFooter className="ion-padding-vertical input-footer insert-comment-mention-wrap">
        {mentionsDropdown}
        <IonItem lines="none">
          <IonButton
            fill="clear"
            shape="round"
            color="medium"
            disabled={isCommentInputDisabled}
            onClick={openPhotoPicker}
          >
            <IonIcon src={image} slot="icon-only" />
          </IonButton>
          <input
            id="comment-upload-image"
            onChange={selectedImage}
            className="d-false"
            accept="image/png,image/jpeg,video/mp4,video/webm,video/quicktime"
            type="file"
          />
          <IonTextarea
            ref={inputValue}
            className="comment-input"
            placeholder="Inserisci un commento"
            autoGrow={true}
            maxlength={MAX_COMMENT_LENGTH}
            spellcheck={true}
            autocapitalize="sentences"
            autocorrect="on"
            disabled={isCommentInputDisabled}
            onKeyUp={handleKeyUp}
            onKeyDown={handleCommentKeyDown}
            onIonInput={handleCommentInput}
            onIonFocus={async () => {
              try {
                nativeInputRef.current =
                  await inputValue.current?.getInputElement?.();
              } catch (e) {
                // best-effort
              }
            }}
          ></IonTextarea>
          <IonButton
            fill="clear"
            shape="round"
            disabled={isCommentInputDisabled}
            color="medium"
            onClick={handleClickSendCommentButton}
          >
            <IonIcon src={sendSharp} slot="icon-only" />
          </IonButton>
        </IonItem>
        {loadedImage && (
          <div className="comment-image-preview">
            <img src={loadedImage} alt="" />
            {imageRotateBar}
            <span onClick={deleteLoadedImage}>Elimina</span>
          </div>
        )}
        {loadedVideo && (
          <div className="comment-image-preview">
            <LocalVideoPreview src={loadedVideo} />
            <span onClick={deleteLoadedImage}>Elimina</span>
          </div>
        )}
      </IonFooter>
      <IonToast isOpen={toast.open} message={toast.message} duration={3000} />
      {photoActionSheet}
      <PermissionPopupModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        message={popupText}
      />
    </>
  );
};
